import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ATTACHMENT_KINDS } from '@toxofollow/shared';
import { api, downloadFile, getCsrfToken, ApiError } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import { Card, Button, Badge, Table, Field, inputClass } from '../components/ui.js';
import { Select, QueryState, ErrorState } from '../components/form.js';

interface AttachmentRow {
  id: string;
  kind: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  linkedType: string | null;
  linkedId: string | null;
  createdAt: string;
}

const LINKED_TYPES = ['lab', 'appointment', 'medication', 'note', 'profile', 'serology'] as const;

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function Attachments() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState('lab_pdf');
  const [linkedType, setLinkedType] = useState('');
  const [linkedId, setLinkedId] = useState('');

  const q = useQuery({ queryKey: ['attachments'], queryFn: () => api.get<{ attachments: AttachmentRow[] }>('/attachments') });

  // Raw multipart upload: must set the CSRF header ourselves (the api client only adds it for JSON requests).
  const uploadM = useMutation({
    mutationFn: async () => {
      const file = fileRef.current?.files?.[0];
      if (!file) throw new Error(t('attach.noFileSelected'));
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', kind);
      if (linkedType) fd.append('linkedType', linkedType);
      if (linkedId.trim()) fd.append('linkedId', linkedId.trim());
      const csrf = getCsrfToken();
      const res = await fetch('/api/attachments', {
        method: 'POST',
        credentials: 'include',
        headers: csrf ? { 'x-csrf-token': csrf } : {},
        body: fd,
      });
      if (!res.ok) {
        const text = await res.text();
        let err: { code?: string; message?: string } = {};
        try {
          err = (text ? JSON.parse(text) : {}).error ?? {};
        } catch {
          /* non-json */
        }
        throw new ApiError(res.status, err.code ?? 'upload_failed', err.message ?? 'Upload failed');
      }
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['attachments'] });
      if (fileRef.current) fileRef.current.value = '';
      setLinkedId('');
    },
  });

  const downloadM = useMutation({
    mutationFn: (a: AttachmentRow) => downloadFile(`/attachments/${a.id}/download`, a.filename),
  });
  const deleteM = useMutation({
    mutationFn: (id: string) => api.del(`/attachments/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['attachments'] }),
  });

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-800 mb-4">{t('attach.title')}</h1>

      <Card title={t('attach.upload')}>
        <p className="text-xs text-slate-500 mb-3">
          {t('attach.notice')}
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            uploadM.mutate();
          }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4">
            <Field label={t('attach.file')}>
              <input ref={fileRef} type="file" className="text-sm" required />
            </Field>
            <Field label={t('attach.kind')}>
              <Select value={kind} onChange={(e) => setKind(e.target.value)}>
                {ATTACHMENT_KINDS.map((k) => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </Select>
            </Field>
            <Field label={t('attach.linkedType')}>
              <Select value={linkedType} onChange={(e) => setLinkedType(e.target.value)}>
                <option value="">—</option>
                {LINKED_TYPES.map((lt) => (
                  <option key={lt} value={lt}>{lt}</option>
                ))}
              </Select>
            </Field>
            <Field label={t('attach.linkedId')}>
              <input className={inputClass} value={linkedId} onChange={(e) => setLinkedId(e.target.value)} placeholder="uuid" />
            </Field>
          </div>
          {uploadM.isError && <div className="mb-3"><ErrorState error={uploadM.error} fallback={t('common.error')} /></div>}
          <Button type="submit" disabled={uploadM.isPending}>{uploadM.isPending ? '…' : t('attach.upload')}</Button>
        </form>
      </Card>

      <Card title="">
        <QueryState
          isLoading={q.isLoading}
          isError={q.isError}
          error={q.error}
          errorFallback={t('common.error')}
          isEmpty={(q.data?.attachments.length ?? 0) === 0}
          emptyMessage="—"
        >
          <Table headers={[t('attach.file'), t('attach.kind'), t('common.size'), t('attach.linked'), '']}>
            {q.data?.attachments.map((a) => (
              <tr key={a.id} className="border-b border-slate-100">
                <td className="py-2 pr-4">
                  {a.filename}
                  <div className="text-xs text-slate-400">{a.createdAt?.slice(0, 10)}</div>
                </td>
                <td className="py-2 pr-4"><Badge tone="info">{a.kind}</Badge></td>
                <td className="py-2 pr-4 text-slate-500">{fmtSize(a.sizeBytes)}</td>
                <td className="py-2 pr-4 text-xs text-slate-400">{a.linkedType ?? '—'}</td>
                <td className="py-2 pr-4 whitespace-nowrap space-x-3">
                  <button className="text-xs underline text-brand" onClick={() => downloadM.mutate(a)}>
                    {t('common.download')}
                  </button>
                  <button
                    className="text-xs underline text-danger"
                    onClick={() => {
                      if (window.confirm(t('common.confirm'))) deleteM.mutate(a.id);
                    }}
                  >
                    {t('common.delete')}
                  </button>
                </td>
              </tr>
            ))}
          </Table>
        </QueryState>
        {downloadM.isError && <div className="mt-2"><ErrorState error={downloadM.error} fallback={t('common.error')} /></div>}
      </Card>
    </div>
  );
}
